import {
  GetIssueResponseDtoActionProgressEnum,
  GetIssueResponseDtoStatusEnum,
} from '@issuemanager/api-client';
import { DayjsValue, disablePast } from '@issuemanager/apps/client/utils';
import {
  ACTION_DESCRIPTION_LENGTH_MAX,
  ACTION_TITLE_LENGTH_MAX,
  ActionStatus,
  ISSUE_WORKFLOW_EXECUTION_TYPE,
  ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES,
  IssueTypeValues,
  IssueWorkflowExecutionType,
} from '@issuemanager/constants';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { z } from 'zod';

export type GetManageIssueFormSchemaArgs = {
  t: TFunction<('issue' | 'settings' | 'action' | 'common')[], undefined>;
  timezoneOffset?: number;
  SLAs: any[];
  isDateEarlierThanSLA: (SLAs: any[], severity: string, value: any) => boolean;
  issueStatus: GetIssueResponseDtoStatusEnum | undefined;
  issueActionProgress: GetIssueResponseDtoActionProgressEnum | undefined;
  isDraftIssue?: boolean;
};

const actionSchema = z.object({
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  dueDate: z
    .any()
    .transform((val) => (dayjs.isDayjs(val) ? val.toDate() : val))
    .nullable()
    .optional(),
  ownerId: z.string().nullable().optional(),
  approverId: z.string().nullable().optional(),
  status: z.any(), // Used for carrying additional form information, no type needed
  customId: z.any(), // Used for carrying additional form information, no type needed
});

export const baseSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  issueType: z.enum(IssueTypeValues),
  ownerId: z.any().transform((id) => id ?? ''),
  severity: z.any().transform((s) => s ?? ''),
  dueDate: z
    .any()
    .transform((val) => (dayjs.isDayjs(val) ? val.toDate() : val)),
  managerId: z.any().transform((id) => id ?? ''),
  approverId: z.any().transform((id) => id ?? ''),
  executionType: z.enum(ISSUE_WORKFLOW_EXECUTION_TYPE_VALUES),
  actions: z.array(actionSchema).optional(),
  impact: z.string().optional(),
  rootCause: z.string().optional(),
  recommendations: z.string().optional(),
  managementResponse: z.string().optional(),
  remediation: z.string().optional(),
  classification: z.any().transform((c) => c ?? ''),
  reviewStatus: z.any().transform((r) => r ?? ''),
  resolution: z.any().transform((r) => r ?? ''),
  publishedDate: z
    .any()
    .transform((val) => (dayjs.isDayjs(val) ? val.toDate() : val))
    .optional(),
  includeInAuditReport: z.boolean().optional(),
});

type ActionSchemaType = z.infer<typeof actionSchema>;

type IssueDueDateDisablePastValidationArgs = Pick<
  GetManageIssueFormSchemaArgs,
  'timezoneOffset' | 't'
> & {
  ctx: z.RefinementCtx;
  dueDate: any;
  disablePast: (
    timezoneOffset: number,
    value: DayjsValue,
    maxDate?: DayjsValue,
  ) => boolean;
};
type IssueDueDateEarlierThanSLAPastValidationArgs = Pick<
  GetManageIssueFormSchemaArgs,
  'isDateEarlierThanSLA' | 'SLAs' | 't'
> & { ctx: z.RefinementCtx; data: { dueDate: any; severity?: string | null } };
type ActionTitleValidationsArgs = Pick<
  GetManageIssueFormSchemaArgs,
  'isDateEarlierThanSLA' | 'SLAs' | 't'
> & {
  ctx: z.RefinementCtx;
  data: {
    action: ActionSchemaType;
    actions: ActionSchemaType[];
    index: number;
    standardizedTitles: Set<string>;
  };
};
type ActionDueDateValidationsArgs = Pick<
  GetManageIssueFormSchemaArgs,
  'timezoneOffset' | 'SLAs' | 't'
> & {
  ctx: z.RefinementCtx;
  data: {
    dueDate: any;
    action: ActionSchemaType;
    index: number;
  };
  skipPastDateValidation?: boolean;
};
export type ActionSequentialDueDateValidationArgs = Pick<
  GetManageIssueFormSchemaArgs,
  't'
> & {
  ctx: z.RefinementCtx;
  data: {
    dueDate: any;
    actions: ActionSchemaType[];
    action: ActionSchemaType;
    index: number;
    executionType?: IssueWorkflowExecutionType;
  };
};
type ActionsAtLeastOneValidationArgs = Pick<
  GetManageIssueFormSchemaArgs,
  't'
> & {
  ctx: z.RefinementCtx;
  actions?: ActionSchemaType[];
  isDraftIssue?: boolean;
};
type ActionDescriptionValidationArgs = Pick<
  GetManageIssueFormSchemaArgs,
  't'
> & {
  data: {
    description?: string | null;
    index: number;
  };
  ctx: z.RefinementCtx;
};
type ActionDueDateValidationArgs = Pick<GetManageIssueFormSchemaArgs, 't'> & {
  data: {
    dueDate: any;
    index: number;
  };
  ctx: z.RefinementCtx;
};
type ActionOwnerValidationArgs = Pick<GetManageIssueFormSchemaArgs, 't'> & {
  data: {
    ownerId?: string | null;
    index: number;
  };
  ctx: z.RefinementCtx;
};
type ActionApproverValidationArgs = Pick<GetManageIssueFormSchemaArgs, 't'> & {
  data: {
    approverId?: string | null;
    status: ActionStatus;
    index: number;
  };
  ctx: z.RefinementCtx;
};

export function issueDueDateDisablePastValidation({
  ctx,
  disablePast,
  t,
  timezoneOffset,
  dueDate,
}: IssueDueDateDisablePastValidationArgs) {
  if (dueDate) {
    if (!disablePast(timezoneOffset ?? 0, dueDate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dueDate'],
        message: t('issue:form.errorMessages.dueDate.disablePast'),
      });
    }
  }
}

export function issueDueDateEarlierThanSLAPastValidation({
  ctx,
  SLAs,
  isDateEarlierThanSLA,
  t,
  data: { dueDate, severity },
}: IssueDueDateEarlierThanSLAPastValidationArgs) {
  if (
    SLAs.length > 0 &&
    severity &&
    !isDateEarlierThanSLA(SLAs, severity, dueDate)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dueDate'],
      message: t('issue:form.errorMessages.dueDate.earlierThanSLA'),
    });
  }
}

export function actionTitleValidations({
  ctx,
  data: { index, action, actions },
  t,
}: ActionTitleValidationsArgs) {
  // Trim the incoming value to ensure validation is performed on the normalized string.
  const trimmedTitle = (action.title ?? '').trim();

  // Title is required after trimming
  if (trimmedTitle.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [`actions.${index}.title`],
      message: t('action:form.errorMessages.title.required'),
    });
    return;
  }

  // Max-length validation (<= ACTION_TITLE_LENGTH_MAX characters after trimming)
  if (trimmedTitle.length > ACTION_TITLE_LENGTH_MAX) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [`actions.${index}.title`],
      message: t('action:form.errorMessages.title.maxLength'),
    });
  }

  // Unique title validation (case-insensitive, after trimming)
  const normalizedTitleLower = trimmedTitle.toLowerCase();
  if (
    actions
      .filter((a) => a.customId !== action.customId)
      .some(
        (a) => (a.title ?? '').trim().toLowerCase() === normalizedTitleLower,
      )
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [`actions.${index}.title`],
      message: t('action:form.errorMessages.title.unique'),
    });
  }
}

export function actionDueDateValidations({
  ctx,
  data: { dueDate, action, index },
  t,
  timezoneOffset,
  skipPastDateValidation = false,
}: ActionDueDateValidationsArgs) {
  if (action.dueDate) {
    // Past date validation - skip when updating existing issues
    if (
      !skipPastDateValidation &&
      !disablePast(timezoneOffset ?? 0, action.dueDate)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [`actions.${index}.dueDate`],
        message: t('action:form.errorMessages.dueDate.disablePast'),
      });
    }
    // Due date must be earlier than the issue due date
    if (dueDate && !disablePast(timezoneOffset ?? 0, dueDate, action.dueDate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [`actions.${index}.dueDate`],
        message: t('action:form.errorMessages.dueDate.earlierThanIssueDueDate'),
      });
    }
  }
}

export function actionSequentialDueDateValidation({
  data: { executionType, index, actions, action },
  ctx,
  t,
}: ActionSequentialDueDateValidationArgs) {
  if (executionType === ISSUE_WORKFLOW_EXECUTION_TYPE.SEQUENTIAL && index > 0) {
    const prevDueDate = actions?.[index - 1].dueDate;
    if (
      prevDueDate &&
      action.dueDate &&
      new Date(action.dueDate) < new Date(prevDueDate)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [`actions.${index}.dueDate`],
        message: t(
          'action:form.errorMessages.dueDate.earlierThanPrevActionDueDate',
        ),
      });
    }
  }
}

export function actionsAtLeastOneValidation({
  ctx,
  actions,
  t,
  isDraftIssue,
}: ActionsAtLeastOneValidationArgs): void {
  // Actions are optional for draft issues with draftIssues feature flag enabled
  if (isDraftIssue) {
    return;
  }

  // at least one action is required for non-draft issues
  if (!actions || actions.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['actions'],
      message: t('issue:form.errorMessages.actions.createBeforeInitiate'),
    });
  }
}

export function actionDescriptionValidation({
  ctx,
  data: { description, index },
  t,
}: ActionDescriptionValidationArgs) {
  // all fields are required
  if (!description) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [`actions.${index}.description`],
      message: t('action:form.errorMessages.description.required'),
    });
  } else if (description.length > ACTION_DESCRIPTION_LENGTH_MAX) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [`actions.${index}.description`],
      message: t('action:form.errorMessages.description.maxLength'),
    });
  }
}

export function actionDueDateValidation({
  data: { dueDate, index },
  ctx,
  t,
}: ActionDueDateValidationArgs) {
  if (!dueDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [`actions.${index}.dueDate`],
      message: t('action:form.errorMessages.dueDate.required'),
    });
  }
}

export function actionOwnerValidation({
  data: { ownerId, index },
  ctx,
  t,
}: ActionOwnerValidationArgs) {
  if (!ownerId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [`actions.${index}.ownerId`],
      message: t('action:form.errorMessages.owner.required'),
    });
  }
}

export function actionApproverValidation({
  data: { approverId, status, index },
  ctx,
  t,
}: ActionApproverValidationArgs) {
  // Approver required only if status is "ToBeApproved"
  if (approverId === undefined && status === ActionStatus.TO_BE_APPROVED) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [`actions.${index}.approverId`],
      message: t('action:form.errorMessages.approver.required'),
    });
  }
}
